import React, { useEffect } from 'react';
import { Grid2, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useGetShow } from '@openapi/shows/shows';
import { useGetUsers } from '@openapi/users/users';
import useNotify from '../../../hooks/useNotify';
import ViatorProductsEditor from './ViatorProductsEditor';

// Sentinel group option for agents that belong to no commission group, so they
// stay selectable in the group-first picker.
const NO_GROUP = '__NO_GROUP__';

function ViatorForm({ values, setFieldValue }) {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    // commissionGroup is UI-only (never persisted - onSubmit only sends settings),
    // but it lives in Formik values so the parent's validate() can require an
    // affiliate agent once a group is picked.
    const commissionGroup = values.commissionGroup ?? null;

    const { data: showData } = useGetShow(Number(values.showId), {
        query: { enabled: !!values.showId },
    });
    const venueId = showData?.venueId;

    // showId is required so the backend populates each user's commissionGroupNames.
    const { data: usersData } = useGetUsers(
        venueId,
        { pageSize: 1000, showId: values.showId },
        {
            query: {
                enabled: !!venueId && !!values.showId,
                onError: notifyError,
            },
        },
    );
    const agents = usersData?.users || [];

    const selectedAgentId = values.settings?.agent_user_id || null;
    const savedAgent = agents.find((a) => a.id === selectedAgentId) || null;

    // When editing an integration that already has an agent, pre-select that
    // agent's group so it shows in the filtered list on first render.
    useEffect(() => {
        if (values.commissionGroup !== undefined || !savedAgent) return;
        setFieldValue('commissionGroup', savedAgent.commissionGroupNames?.[0] ?? NO_GROUP);
    }, [savedAgent, values.commissionGroup, setFieldValue]);

    const commissionGroups = [
        ...new Set(agents.flatMap((a) => a.commissionGroupNames || [])),
    ].sort();

    // Keep agents without a commission group selectable (they earn no commission).
    const hasGrouplessAgents = agents.some((a) => !a.commissionGroupNames?.length);
    const groupOptions = hasGrouplessAgents ? [...commissionGroups, NO_GROUP] : commissionGroups;

    const groupLabel = (group) =>
        group === NO_GROUP ? t('integrations.columns.noCommissionGroup') : group;

    const filteredAgents = !commissionGroup
        ? []
        : commissionGroup === NO_GROUP
          ? agents.filter((a) => !a.commissionGroupNames?.length)
          : agents.filter((a) => a.commissionGroupNames?.includes(commissionGroup));

    const selectedAgent = filteredAgents.find((a) => a.id === selectedAgentId) || null;

    // A commission group is just a filter for picking the agent, so selecting one
    // without an agent leaves the integration in a half-configured state.
    const agentMissing = !!commissionGroup && !selectedAgentId;

    return (
        <>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('integrations.columns.accessKey')}
                    value={values.settings?.accessKey || ''}
                    onChange={(ev) =>
                        setFieldValue('settings', {
                            ...values.settings,
                            accessKey: ev.target.value,
                        })
                    }
                    placeholder="Enter your Viator API access key"
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <TextField
                    fullWidth
                    label={t('integrations.columns.supplierId')}
                    value={values.settings?.supplierId || ''}
                    onChange={(ev) =>
                        setFieldValue('settings', {
                            ...values.settings,
                            supplierId: ev.target.value,
                        })
                    }
                    placeholder="Enter your Viator supplier ID"
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Autocomplete
                    fullWidth
                    options={groupOptions}
                    value={commissionGroup}
                    getOptionLabel={groupLabel}
                    onChange={(_, val) => {
                        setFieldValue('commissionGroup', val);
                        setFieldValue('settings', {
                            ...values.settings,
                            agent_user_id: null,
                        });
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label={t('integrations.columns.commissionGroup')} />
                    )}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Autocomplete
                    fullWidth
                    options={filteredAgents}
                    getOptionLabel={(o) => o.name || ''}
                    value={selectedAgent}
                    disabled={!commissionGroup}
                    onChange={(_, val) =>
                        setFieldValue('settings', {
                            ...values.settings,
                            agent_user_id: val ? val.id : null,
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('integrations.columns.affiliateAgent')}
                            error={agentMissing}
                            helperText={
                                agentMissing
                                    ? t('integrations.validation.affiliateAgentRequired')
                                    : ''
                            }
                        />
                    )}
                />
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <ViatorProductsEditor
                    showId={values.showId}
                    values={values}
                    setFieldValue={setFieldValue}
                />
            </Grid2>
        </>
    );
}

export default ViatorForm;
